import { motion } from 'framer-motion';

import { Button, Divider, fadeIn, FormGroup, FormRow, Input, PageContainer, SectionTitle, useGoBack, Label, SubSectionTitle, ButtonSuccess, Table, TableHeader, TableRow, TableCell, Select, TableFormGroup, Modal, ModalContent, ModalHeader, ModalFooter, CloseButton, Textarea } from "../styles/PageStyles";
import { faChevronCircleLeft, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { faSave } from '@fortawesome/free-regular-svg-icons';

import { createTool, getToolTypes, getToolById, updateTool, testEmailConnection } from '../services/toolService';
import { useToast } from '../components/common/ToastProvider';

export const ManageTools = () => {
    const [toolData, setToolData] = useState({});
    const [toolParameters, setToolParameters] = useState([{ name: 'test', value: 'testvalue' }]);
    const [toolTypes, setToolTypes] = useState([]);

    // Provider fields for Email Tool
    const [emailProvider, setEmailProvider] = useState('microsoft');
    const [emailClientId, setEmailClientId] = useState('');
    const [emailClientSecret, setEmailClientSecret] = useState('');
    const [emailTenantId, setEmailTenantId] = useState('');
    const [emailRefreshToken, setEmailRefreshToken] = useState('');
    const [emailFromEmail, setEmailFromEmail] = useState('');
    const [isTesting, setIsTesting] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [testTo, setTestTo] = useState('');
    const [testSubject, setTestSubject] = useState('');
    const [testBody, setTestBody] = useState('');

    useEffect(() => {
        const fetchTypes = async () => {
            try {
                const types = await getToolTypes();
                setToolTypes(types || ['system', 'builtin', 'mcp_internal', 'mcp_external', 'email']);
            } catch (error) {
                console.error("Failed to fetch tool types:", error);
                setToolTypes(['system', 'builtin', 'mcp_internal', 'mcp_external', 'email']);
            }
        };
        fetchTypes();
    }, []);

    const userData = localStorage.getItem('userData');
    const { toolId } = useParams()
    const { showToast } = useToast();

    useEffect(() => {
        if (toolId && toolId !== 'new') {
            const fetchTool = async () => {
                try {
                    const tool = await getToolById(toolId);
                    if (tool) {
                        setToolData(tool);
                        if (tool.tool_type === 'email' && tool.tool_parameters) {
                            const params = tool.tool_parameters;
                            setEmailProvider(params.provider_type || 'microsoft');
                            setEmailFromEmail(params.from_email || '');
                            const creds = params.credentials || {};
                            setEmailClientId(creds.client_id || '');
                            setEmailClientSecret(creds.client_secret || '');
                            setEmailTenantId(creds.tenant_id || '');
                            setEmailRefreshToken(creds.refresh_token || '');
                        } else if (tool.tool_parameters) {
                            const paramsArray = Object.entries(tool.tool_parameters).map(([name, value]) => ({
                                name,
                                value
                            }));
                            setToolParameters(paramsArray);
                        } else {
                            setToolParameters([]);
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch tool by ID:", error);
                    showToast({
                        message: 'Failed to load tool details.',
                        type: 'error'
                    });
                }
            };
            fetchTool();
        } else {
            setToolData({ version: 1 });
            setToolParameters([{ name: 'test', value: 'testvalue' }]);
            setEmailProvider('microsoft');
            setEmailClientId('');
            setEmailClientSecret('');
            setEmailTenantId('');
            setEmailRefreshToken('');
            setEmailFromEmail('');
        }
    }, [toolId]);

    const addToolParams = () => {
        const newParam = { name: '', value: '' }
        setToolParameters((prev) => [...prev, newParam])
    }

    const handleCreateTool = async () => {
        if (!toolData?.name) {
            showToast({
                message: 'Name is required.',
                type: 'warning'
            });
            return;
        }
        if (!toolData?.tool_type) {
            showToast({
                message: 'Tool Type is required.',
                type: 'warning'
            });
            return;
        }

        let toolParamsObject = {};
        if (toolData?.tool_type === 'email') {
            if (!emailFromEmail) {
                showToast({
                    message: 'From Email is required.',
                    type: 'warning'
                });
                return;
            }
            if (!emailClientId) {
                showToast({
                    message: 'Client ID is required.',
                    type: 'warning'
                });
                return;
            }
            if (!emailClientSecret) {
                showToast({
                    message: 'Client Secret is required.',
                    type: 'warning'
                });
                return;
            }
            if (emailProvider === 'microsoft' && !emailTenantId) {
                showToast({
                    message: 'Tenant ID is required for Outlook.',
                    type: 'warning'
                });
                return;
            }
            if (emailProvider === 'google' && !emailRefreshToken) {
                showToast({
                    message: 'Refresh Token is required for Gmail.',
                    type: 'warning'
                });
                return;
            }

            toolParamsObject = {
                provider_type: emailProvider,
                from_email: emailFromEmail,
                credentials: {
                    client_id: emailClientId,
                    client_secret: emailClientSecret,
                    ...(emailProvider === 'microsoft' ? { tenant_id: emailTenantId } : { refresh_token: emailRefreshToken })
                }
            };
        } else {
            const keys = toolParameters.map(p => p.name);
            const hasDuplicate = new Set(keys).size !== keys.length;
            if (hasDuplicate) {
                showToast({
                    message: 'Keys should be unique.',
                    type: 'warning'
                });
                return;
            }

            toolParamsObject = toolParameters.reduce((acc, item) => {
                if (item.name) {
                    acc[item.name] = item.value;
                }
                return acc;
            }, {});
        }

        try {
            const isNew = !toolId || toolId === 'new';
            const nextVersion = isNew ? 1 : (toolData?.version ? parseInt(toolData.version, 10) + 1 : 2);

            const toolStructure = {
                company_cd: JSON.parse(userData).companyCd,
                ...toolData,
                code: toolData?.name,
                tool_parameters: toolParamsObject,
                version: nextVersion
            };

            if (toolId && toolId !== 'new') {
                toolStructure.id = parseInt(toolId);
            }

            console.log("ToolStructure", toolStructure);

            let response;
            if (toolId && toolId !== 'new') {
                response = await updateTool(toolId, toolStructure);
            } else {
                response = await createTool(toolStructure);
            }
            console.log("Tool Response", response);

            showToast({
                message: 'Tool saved successfully.',
                type: 'success'
            });

            setTimeout(() => {
                goBack();
            }, 1000);
        } catch (error) {
            console.error("Tool Saving Error", error);
            showToast({
                message: error.response?.data?.detail || 'Failed to save tool.',
                type: 'error'
            });
        }
    };

    const handleToolParamChange = (e, field, paramIdx) => {
        const value = e.target.value;

        setToolParameters(prev =>
            prev.map((item, index) =>
                index === paramIdx
                    ? { ...item, [field]: value }
                    : item
            )
        );
    };

    const removeToolParam = (indexToRemove) => {
        setToolParameters(prev =>
            prev.filter((_, index) => index !== indexToRemove)
        );
    };

    const openTestConnectionModal = () => {
        if (!emailFromEmail) {
            showToast({
                message: 'From Email is required.',
                type: 'warning'
            });
            return;
        }
        if (!emailClientId) {
            showToast({
                message: 'Client ID is required.',
                type: 'warning'
            });
            return;
        }
        if (!emailClientSecret) {
            showToast({
                message: 'Client Secret is required.',
                type: 'warning'
            });
            return;
        }
        if (emailProvider === 'microsoft' && !emailTenantId) {
            showToast({
                message: 'Tenant ID is required for Outlook.',
                type: 'warning'
            });
            return;
        }
        if (emailProvider === 'google' && !emailRefreshToken) {
            showToast({
                message: 'Refresh Token is required for Gmail.',
                type: 'warning'
            });
            return;
        }

        setTestTo('');
        setTestSubject('Test Email Connection');
        setTestBody('This is a test email to verify connection settings.');
        setShowTestModal(true);
    };

    const handleTestConnection = async () => {
        if (!testTo) {
            showToast({
                message: 'Recipient email is required.',
                type: 'warning'
            });
            return;
        }
        if (!testSubject) {
            showToast({
                message: 'Subject is required.',
                type: 'warning'
            });
            return;
        }
        if (!testBody) {
            showToast({
                message: 'Body is required.',
                type: 'warning'
            });
            return;
        }

        setIsTesting(true);
        try {
            const payload = {
                provider_type: emailProvider,
                from_email: emailFromEmail,
                credentials: {
                    client_id: emailClientId,
                    client_secret: emailClientSecret,
                    ...(emailProvider === 'microsoft' ? { tenant_id: emailTenantId } : { refresh_token: emailRefreshToken })
                },
                to: testTo,
                subject: testSubject,
                body: testBody
            };

            const response = await testEmailConnection(payload);
            if (response && response.success) {
                showToast({
                    message: response.message || 'Connection successful!',
                    type: 'success'
                });
                setShowTestModal(false);
            } else {
                showToast({
                    message: response?.message || 'Connection test failed.',
                    type: 'error'
                });
            }
        } catch (error) {
            console.error("Connection Test Error", error);
            showToast({
                message: error.response?.data?.message || error.message || 'Failed to test email connection.',
                type: 'error'
            });
        } finally {
            setIsTesting(false);
        }
    };

    const goBack = useGoBack();


    return (
        <PageContainer>
            <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeIn}
            >
                <SectionTitle>
                    <Button onClick={goBack} secondary ><FontAwesomeIcon icon={faChevronCircleLeft} /></Button>
                    {toolId !== 'new' ? (
                        <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                            Edit v - {toolData?.version || 1}
                        </span>
                    ) : (
                        <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                            Add Tool
                        </span>
                    )}
                </SectionTitle>
                <Divider />

                <FormRow>
                    <FormGroup>
                        <Label>Name</Label>
                        <Input type="text"
                            value={toolData?.name || ''}
                            onChange={(e) => setToolData({ ...toolData, name: e.target.value })} />
                    </FormGroup>

                    <FormGroup>
                        <Label>Tool Type</Label>
                        <Select
                            value={toolData?.tool_type || ''}
                            onChange={(e) => setToolData({ ...toolData, tool_type: e.target.value })}
                        >
                            <option value="">Select Tool Type</option>
                            {toolTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </Select>
                    </FormGroup>
                </FormRow>

                <FormGroup>
                    <Label>Description</Label>
                    <Input type="text"
                        value={toolData?.description || ''}
                        onChange={(e) => setToolData({ ...toolData, description: e.target.value })} />
                </FormGroup>

                {toolData?.tool_type === 'email' ? (
                    <div>
                        <Divider />
                        <SubSectionTitle>Email Provider Configuration</SubSectionTitle>
                        <FormRow>
                            <FormGroup>
                                <Label>Provider</Label>
                                <Select
                                    value={emailProvider}
                                    onChange={(e) => setEmailProvider(e.target.value)}
                                >
                                    <option value="microsoft">OUTLOOK</option>
                                    <option value="google">GMAIL</option>
                                </Select>
                            </FormGroup>
                            <FormGroup>
                                <Label>From Email</Label>
                                <Input
                                    type="email"
                                    value={emailFromEmail}
                                    onChange={(e) => setEmailFromEmail(e.target.value)}
                                    placeholder="Enter sender email"
                                />
                            </FormGroup>
                        </FormRow>
                        <FormRow>
                            <FormGroup>
                                <Label>Client ID</Label>
                                <Input
                                    type="text"
                                    value={emailClientId}
                                    onChange={(e) => setEmailClientId(e.target.value)}
                                    placeholder="Enter Client ID"
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Client Secret</Label>
                                <Input
                                    type="password"
                                    value={emailClientSecret}
                                    onChange={(e) => setEmailClientSecret(e.target.value)}
                                    placeholder="Enter Client Secret"
                                />
                            </FormGroup>
                        </FormRow>
                        {emailProvider === 'microsoft' ? (
                            <FormRow>
                                <FormGroup style={{ maxWidth: '50%' }}>
                                    <Label>Tenant ID</Label>
                                    <Input
                                        type="text"
                                        value={emailTenantId}
                                        onChange={(e) => setEmailTenantId(e.target.value)}
                                        placeholder="Enter Tenant ID"
                                    />
                                </FormGroup>
                            </FormRow>
                        ) : (
                            <FormRow>
                                <FormGroup style={{ maxWidth: '50%' }}>
                                    <Label>Refresh Token</Label>
                                    <Input
                                        type="text"
                                        value={emailRefreshToken}
                                        onChange={(e) => setEmailRefreshToken(e.target.value)}
                                        placeholder="Enter Refresh Token"
                                    />
                                </FormGroup>
                            </FormRow>
                        )}
                    </div>
                ) : (
                    <div>
                        <Divider />
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem' }}>
                            <SubSectionTitle>Tool Parameters</SubSectionTitle>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: '2px', alignItems: 'center' }}>
                                <Input type="text" placeholder="Search parameters..." />
                                <ButtonSuccess onClick={() => addToolParams()}><FontAwesomeIcon icon={faPlus} /> Params</ButtonSuccess>
                            </div>
                        </div>

                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader>Name </TableHeader>
                                    <TableHeader>Value</TableHeader>
                                    <TableHeader>Actions</TableHeader>
                                </tr>
                            </thead>
                            <tbody>
                                {toolParameters.length > 0 && toolParameters.map((param, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <TableFormGroup>
                                                <Input type="text" value={param?.name || ''}
                                                    onChange={(e) => handleToolParamChange(e, 'name', index)} />
                                            </TableFormGroup>
                                        </TableCell>
                                        <TableCell>
                                            <TableFormGroup>
                                                <Input type="text" value={param?.value || ''}
                                                    onChange={(e) => handleToolParamChange(e, 'value', index)} />
                                            </TableFormGroup>
                                        </TableCell>

                                        <TableCell>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <Button onClick={() => { }} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', }} > Edit </Button>
                                                <Button onClick={() => removeToolParam(index)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', color: 'white', backgroundColor: '#ff4444', borderColor: '#ff4444' }}>Remove</Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
                <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
                    <Button primary onClick={handleCreateTool}><FontAwesomeIcon icon={faSave} /> Save</Button>
                    {toolData?.tool_type === 'email' && (
                        <Button type="button" onClick={openTestConnectionModal} disabled={isTesting}>
                            Test Connection
                        </Button>
                    )}
                </div>

                {showTestModal && (
                    <Modal onClick={() => setShowTestModal(false)}>
                        <ModalContent style={{ height: 'auto', maxHeight: '90vh', maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
                            <ModalHeader>
                                <span style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Test Email Connection</span>
                                <CloseButton onClick={() => setShowTestModal(false)}>✖</CloseButton>
                            </ModalHeader>
                            <Divider />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                                <FormGroup style={{ marginBottom: '1rem' }}>
                                    <Label>Recipient Email (To)</Label>
                                    <Input
                                        type="email"
                                        value={testTo}
                                        onChange={(e) => setTestTo(e.target.value)}
                                        placeholder="recipient@example.com"
                                    />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: '1rem' }}>
                                    <Label>Subject</Label>
                                    <Input
                                        type="text"
                                        value={testSubject}
                                        onChange={(e) => setTestSubject(e.target.value)}
                                        placeholder="Test email subject"
                                    />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: '1rem' }}>
                                    <Label>Body</Label>
                                    <Textarea
                                        value={testBody}
                                        onChange={(e) => setTestBody(e.target.value)}
                                        placeholder="Enter email body..."
                                    />
                                </FormGroup>
                            </div>
                            <ModalFooter style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                <Button secondary onClick={() => setShowTestModal(false)} disabled={isTesting}>
                                    Cancel
                                </Button>
                                <Button primary onClick={handleTestConnection} disabled={isTesting}>
                                    {isTesting ? 'Sending...' : 'Send Test Email'}
                                </Button>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>
                )}
            </motion.div>
        </PageContainer>
    )
}